// Ping client goes here.

// We want a list of aws regions mapped to their URLs.
// We want to ping all of them and get responses.
// We should have an initialize function that takes in an interval and a callback function.
// We should have a start and stop function.
// The callback function should be called with a map of region to latency.

// AWS regions and their ping endpoints
type RegionUrl = {
  [region: string]: string;
};

// Latency results type
export type LatencyResults = {
  [region: string]: number;
};

// History of latency results for each region
type LatencyHistory = {
  [region: string]: number[];
};

// Number of samples to keep for rolling calculations
const HISTORY_SIZE = 4;

// Module implementation for the ping client
export const pingClient = (() => {
  // Private variables
  const regions: RegionUrl = {
    'us-east-1': 'https://alpha.e1.ping.gibber.com/ping',
    // 'us-east-2': 'https://alpha.e2.ping.gibber.com/ping',
    // 'us-west-1': 'https://alpha.w1.ping.gibber.com/ping',
    'us-west-2': 'https://alpha.w2.ping.gibber.com/ping',
    'eu-central-1': 'https://dynamodb.eu-central-1.amazonaws.com',
  };

  let interval = 60000; // Default interval of 60 seconds
  let callback: (results: LatencyResults) => void = () => {
    // Default no-op callback
    console.log('PingClient: callback not initialized');
  };
  let intervalId: NodeJS.Timeout | null = null;

  // History of latency measurements for each region
  const latencyHistory: LatencyHistory = {};

  // Initialize history storage for each region
  Object.keys(regions).forEach((region) => {
    latencyHistory[region] = [];
  });

  /**
   * Calculate the median value from an array of numbers
   */
  const calculateMedian = (values: number[]): number => {
    if (values.length === 0) return 0;

    // Sort values numerically
    const sorted = [...values].sort((a, b) => a - b);

    // Get the middle value for odd-length arrays, or average of two middle values for even-length
    const middle = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
      return (sorted[middle - 1] + sorted[middle]) / 2;
    } else {
      return sorted[middle];
    }
  };

  /**
   * Ping a single URL and measure the latency
   */
  const pingUrl = async (url: string): Promise<number> => {
    const startTime = performance.now();

    try {
      // Using fetch with a timeout to ping the URL
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      await fetch(url, {
        method: 'GET',
        mode: 'no-cors', // Using no-cors to avoid CORS issues
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return performance.now() - startTime;
    } catch (error) {
      // Return a high latency value on error to indicate connection issues
      return 9999;
    }
  };

  /**
   * Add a latency measurement to the history for a region
   */
  const addLatencyToHistory = (region: string, latency: number): void => {
    // Initialize array if it doesn't exist
    if (!latencyHistory[region]) {
      latencyHistory[region] = [];
    }

    // Add new value
    latencyHistory[region].push(latency);

    // Keep only the last HISTORY_SIZE values
    if (latencyHistory[region].length > HISTORY_SIZE) {
      latencyHistory[region].shift();
    }
  };

  /**
   * Get the current median latency for each region
   */
  const getMedianLatencies = (): LatencyResults => {
    const results: LatencyResults = {};

    Object.keys(latencyHistory).forEach((region) => {
      results[region] = calculateMedian(latencyHistory[region]);
    });

    return results;
  };

  /**
   * Ping all regions and collect the latency results
   */
  const pingAllRegions = async (): Promise<void> => {
    const rawResults: LatencyResults = {};
    const promises = Object.entries(regions).map(async ([region, url]) => {
      const latency = await pingUrl(url);
      rawResults[region] = latency;
      addLatencyToHistory(region, latency);
    });

    await Promise.all(promises);

    // Call the callback with median values
    callback(getMedianLatencies());
  };

  // Public API
  return {
    /**
     * Initialize the ping client
     * @param newInterval - The interval in milliseconds between ping cycles
     * @param newCallback - Function to be called with latency results
     */
    initialize: (newInterval: number, newCallback: (results: LatencyResults) => void): void => {
      interval = newInterval;
      callback = newCallback;
    },

    /**
     * Start pinging the regions at the configured interval
     */
    start: (): void => {
      if (intervalId !== null) {
        return; // Already running
      }

      pingAllRegions(); // Run immediately once
      // Run 1 second later, so we hit cache
      setTimeout(() => {
        pingAllRegions();
      }, 1000);
      intervalId = setInterval(() => pingAllRegions(), interval);
    },

    /**
     * Stop pinging the regions
     */
    stop: (): void => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    },

    /**
     * Get the raw latency history for all regions
     * Useful for debugging or custom calculations
     */
    getLatencyHistory: (): LatencyHistory => {
      return { ...latencyHistory };
    },
  };
})();
